"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.toggle = exports.useOptionItems = exports.useActive = exports.getOptions = exports.children2Items = exports.isGroup = exports.useSelectPrefix = void 0;
var tslib_1 = require("tslib");
var react_1 = require("react");
var ConfigProvider_1 = require("../ConfigProvider");
var utils_1 = require("../utils");
var Option_1 = require("./Option");
/** 获取用于 `Select` 的 `className` 前缀 */
function useSelectPrefix() {
    return ConfigProvider_1.usePrefix('select');
}
exports.useSelectPrefix = useSelectPrefix;
/** 判断给定 `OptionItem` 项是否 `OptionGroupInfo` */
function isGroup(item) {
    return Object.prototype.hasOwnProperty.call(item, 'options');
}
exports.isGroup = isGroup;
/** 基于传给 `Select` / `MultiSelect` 的 `children` 信息得到 `OptionItem` 列表 */
function children2Items(children) {
    var items = [];
    react_1.Children.toArray(children).filter(react_1.isValidElement).forEach(function (_a, i) {
        var type = _a.type, props = _a.props;
        if (type === Option_1.Option) {
            var _b = props, value = _b.value, disabled = _b.disabled, content = _b.children, rootHtmlProps = _b.rootHtmlProps;
            items.push({ value: value, disabled: disabled, content: content, rootHtmlProps: rootHtmlProps });
            return;
        }
        if (type === Option_1.OptionGroup) {
            var _c = props, title = _c.title, groupChildren = _c.children;
            var options = children2Items(groupChildren);
            items.push({ title: title, options: options });
            return;
        }
        if (utils_1.isDev) {
            // eslint-disable-next-line no-console
            console.warn('Unknown element type, expected `<Option>` / `<OptionGroup>`, while got', type);
        }
    });
    return items;
}
exports.children2Items = children2Items;
/** 获取指定 `OptionItem` 列表中所有的 `OptionInfo` 项（包括 `OptionGroupInfo` 项中嵌套的 `OptionInfo`） */
function getOptions(items) {
    return items.reduce(function (options, item) { return (isGroup(item)
        ? tslib_1.__spreadArrays(options, item.options) : tslib_1.__spreadArrays(options, [item])); }, []);
}
exports.getOptions = getOptions;
function useActive(disabled, rootHtmlProps) {
    var _a = react_1.useState(false), active = _a[0], setActive = _a[1];
    var selectorRef = react_1.useRef(null);
    var overlayRef = react_1.useRef(null);
    react_1.useEffect(function () {
        if (disabled)
            return;
        function handleDocumentClick(e) {
            if (!e.target)
                return;
            if (selectorRef.current && selectorRef.current.contains(e.target))
                return;
            if (overlayRef.current && overlayRef.current.contains(e.target))
                return;
            setActive(false);
        }
        window.document.addEventListener('click', handleDocumentClick);
        return function () { return window.document.removeEventListener('click', handleDocumentClick); };
    }, [disabled]);
    // 这里通过 selector 的 handler 而不是在上边 `handleDocumentClick` 中实现
    // 是为了允许 selector 中的内容通过在 `onClick` 中 `stopPropagation` 来避免点击造成 select active 状态变更
    var onSelectorClick = react_1.useCallback(function (e) {
        var _a;
        if (disabled)
            return;
        (_a = rootHtmlProps === null || rootHtmlProps === void 0 ? void 0 : rootHtmlProps.onClick) === null || _a === void 0 ? void 0 : _a.call(rootHtmlProps, e);
        if (e.defaultPrevented)
            return;
        setActive(function (v) { return !v; });
    }, [disabled, rootHtmlProps]);
    return { active: active, setActive: setActive, selectorRef: selectorRef, overlayRef: overlayRef, onSelectorClick: onSelectorClick };
}
exports.useActive = useActive;
function match(text, keyword) {
    return text.toLowerCase().includes(keyword.toLowerCase());
}
function matchOption(option, keyword) {
    return match(utils_1.getText(option.content), keyword);
}
function matchOptions(options, keyword) {
    if (!keyword)
        return options;
    return options.filter(function (option) { return matchOption(option, keyword); });
}
function matchItems(items, keyword) {
    if (!keyword)
        return items;
    var matched = [];
    items.forEach(function (item) {
        if (isGroup(item)) {
            var options = matchOptions(item.options, keyword);
            if (options.length > 0) {
                matched.push(tslib_1.__assign(tslib_1.__assign({}, item), { options: options }));
            }
            return;
        }
        if (matchOption(item, keyword)) {
            matched.push(item);
        }
    });
    return matched;
}
/** 搜索条件发生变更时会触发重新获取数据，这里给这个行为添加一个 debounce，单位 ms */
var fetchDelay = 200;
function saveOptions(map, items) {
    getOptions(items).forEach(function (option) {
        map.set(option.value, option);
    });
    return map;
}
/** 维护 option items 的逻辑（基于初始 children、自定义数据获取逻辑等） */
function useOptionItems(active, keyword, children, fetch) {
    var itemsFromChildren = react_1.useMemo(function () { return children2Items(children); }, [children]);
    var _a = react_1.useState(itemsFromChildren), items = _a[0], _setItems = _a[1];
    var _b = react_1.useState(false), loading = _b[0], setLoading = _b[1];
    var optionsMap = react_1.useState(function () { return saveOptions(new Map(), items); })[0];
    var getOption = react_1.useCallback(function (value) { return optionsMap.get(value); }, [optionsMap]);
    var setItems = react_1.useCallback(function (d) {
        saveOptions(optionsMap, d);
        _setItems(d);
    }, [optionsMap]);
    // keyword 发生变化时获取数据
    react_1.useEffect(function () {
        var cancelled = false;
        function doFetch() {
            return tslib_1.__awaiter(this, void 0, void 0, function () {
                var fetchedItems;
                return tslib_1.__generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            if (fetch == null) {
                                setItems(matchItems(itemsFromChildren, keyword));
                                return [2 /*return*/];
                            }
                            setLoading(true);
                            return [4 /*yield*/, fetch(keyword)];
                        case 1:
                            fetchedItems = _a.sent();
                            if (!cancelled) {
                                setItems(fetchedItems);
                                setLoading(false);
                            }
                            return [2 /*return*/];
                    }
                });
            });
        }
        var timer = setTimeout(doFetch, fetchDelay);
        return function () {
            clearTimeout(timer);
            cancelled = true;
        };
    }, [keyword, itemsFromChildren, fetch, setItems]);
    // select 展开（active: false -> true）时重新获取数据
    react_1.useEffect(function () {
        if (!active)
            return;
        var cancelled = false;
        function doFetch() {
            return tslib_1.__awaiter(this, void 0, void 0, function () {
                var fetchedItems;
                return tslib_1.__generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            if (fetch == null)
                                return [2 /*return*/];
                            setLoading(true);
                            return [4 /*yield*/, fetch(keyword)];
                        case 1:
                            fetchedItems = _a.sent();
                            if (!cancelled) {
                                setItems(fetchedItems);
                                setLoading(false);
                            }
                            return [2 /*return*/];
                    }
                });
            });
        }
        doFetch();
        return function () {
            cancelled = true;
        };
    }, [active, fetch, setItems]); // eslint-disable-line react-hooks/exhaustive-deps
    return { items: items, loading: loading, getOption: getOption };
}
exports.useOptionItems = useOptionItems;
function toggle(values, value) {
    return (values.includes(value)
        ? values.filter(function (v) { return v !== value; }) // 当前项已选中则取消选中
        : tslib_1.__spreadArrays(values, [value]) // 当前项未选中则将其选中
    );
}
exports.toggle = toggle;
//# sourceMappingURL=common.js.map